import React, { useEffect, useRef, useLayoutEffect } from 'react';
// Plyr CSS is now imported globally in index.js
import { getOriginalUrl, getThumbnailUrl } from './utils/photoHelpers';

/**
 * VideoPlayer component for consistent video rendering across modals
 * @param {Object} props
 * @param {string} props.filename - Video filename
 * @param {Object} props.streamData - Pre-fetched stream data from useVideoManager
 * @param {boolean} props.isLoading - Whether stream data is loading
 * @param {boolean} props.showControls - Whether to show video controls
 * @param {boolean} props.autoPlay - Whether to auto-play the video
 * @param {boolean} props.muted - Whether video should be muted
 * @param {string} props.preload - Video preload setting
 * @param {string} props.className - Additional CSS classes
 * @param {function} props.onEnded - Callback when video ends
 * @param {function} props.onError - Callback when video errors
 * @param {function} props.setVideoPlayers - Function to update video players map
 * @returns {JSX.Element}
 */
const VideoPlayer = ({
  filename,
  streamData,
  isLoading,
  showControls = true,
  autoPlay = false,
  muted = false,
  preload = 'metadata',
  className = '',
  onEnded,
  onError,
  setVideoPlayers
}) => {
  const videoRef = useRef(null);
  const currentPlayerRef = useRef(null);

  const videoUrl = streamData?.url || getOriginalUrl(filename);
  const fallbackUrl = getOriginalUrl(filename);

  // Debug logging
  console.log(`VideoPlayer: Rendering ${filename}`, {
    hasStreamData: !!streamData,
    usingStreamUrl: !!streamData?.url,
    videoUrl: videoUrl?.substring(0, 100) + '...',
    fallbackUrl: fallbackUrl?.substring(0, 100) + '...',
    contentType: streamData?.contentType,
    isLoading
  });
  const posterUrl = getThumbnailUrl(filename);

  // This useEffect is now empty - initialization happens in the ref-available effect below

  // Wait for video element ref to be available, then initialize
  useLayoutEffect(() => {
    if (videoRef.current && filename && !currentPlayerRef.current) {
      console.log('VideoPlayer: Video element ref is now available, triggering initialization', { filename, hasRef: !!videoRef.current });
      // Force a re-run of the initialization logic
      const initializePlayer = async () => {
        try {
          console.log('VideoPlayer: Importing Plyr for ref-available initialization...');
          const { default: Plyr } = await import('plyr');
          console.log('VideoPlayer: Plyr imported successfully');

          if (!videoRef.current) return;

          console.log('VideoPlayer: Creating Plyr instance for ref-available case...');

          const controlsConfig = showControls ? [
            'play-large',
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'fullscreen'
          ] : ['play-large'];

          const videoElement = videoRef.current;
          const existingSources = videoElement.querySelectorAll('source');
          existingSources.forEach(source => source.remove());

          videoElement.src = videoUrl;
          videoElement.type = streamData?.contentType || 'video/mp4';

          console.log('VideoPlayer: Video source set for ref-available initialization:', {
            src: videoElement.src.substring(0, 100) + '...',
            type: videoElement.type
          });

          const player = new Plyr(videoElement, {
            controls: controlsConfig,
            settings: showControls ? ['quality', 'speed'] : [],
            speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 2] },
            quality: { default: 1440, options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240] },
            autoplay: autoPlay,
            muted: muted,
            hideControls: true,
            resetOnEnd: true,
            disableContextMenu: true,
            loadSprite: true,
            iconPrefix: 'plyr',
            iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
            blankVideo: 'https://cdn.plyr.io/static/blank.mp4',
            tooltips: { controls: showControls, seek: showControls },
            keyboard: { focused: true, global: false },
            captions: { active: false, language: 'auto', update: false },
            fullscreen: { enabled: showControls, fallback: true, iosNative: true },
            storage: { enabled: true, key: 'plyr' },
            preload: preload,
            clickToPlay: true,
            touch: true,
            invertTime: false,
            displayDuration: true,
            playsinline: true
          });

          console.log('VideoPlayer: Plyr instance created for ref-available case, storing reference...');
          currentPlayerRef.current = player;
          setVideoPlayers(prev => new Map(prev.set(filename, player)));

          if (onEnded) player.on('ended', onEnded);
          if (onError) {
            player.on('error', (error) => {
              console.error('VideoPlayer: Plyr error event:', error);
              onError(error, videoRef.current);
            });
          }

          player.on('ready', () => {
            console.log(`VideoPlayer: Plyr player ready for ref-available ${filename}`);
            const playButton = videoRef.current?.parentElement?.querySelector('.plyr__control--overlaid');
            if (playButton && showControls) {
              playButton.style.opacity = '1';
              playButton.style.visibility = 'visible';
              console.log('VideoPlayer: Play button is visible (ref-available)');
            } else if (playButton && !showControls) {
              playButton.style.opacity = '0';
              playButton.style.visibility = 'hidden';
              console.log('VideoPlayer: Play button hidden for slideshow mode (ref-available)');
            }

            if (videoRef.current) {
              console.log(`VideoPlayer: Video element state (ref-available):`, {
                src: videoRef.current.src?.substring(0, 100) + '...',
                currentSrc: videoRef.current.currentSrc?.substring(0, 100) + '...',
                readyState: videoRef.current.readyState,
                networkState: videoRef.current.networkState,
                sources: Array.from(videoRef.current.querySelectorAll('source')).map(s => ({
                  src: s.src?.substring(0, 100) + '...',
                  type: s.type
                }))
              });
            }
          });

          player.on('error', (error) => {
            console.error(`VideoPlayer: Plyr player error for ref-available ${filename}:`, error);
          });

        } catch (error) {
          console.error(`VideoPlayer: Failed to initialize Plyr for ref-available ${filename}:`, error);
          if (videoRef.current) {
            videoRef.current.controls = true;
            videoRef.current.parentElement?.classList.add('video-player-fallback');
            console.log('VideoPlayer: Added native controls as fallback (ref-available)');
          }
        }
      };

      initializePlayer();
    }
  }, [filename, showControls, autoPlay, muted, preload, onEnded, onError, setVideoPlayers, videoUrl, streamData]); // This will run when props change

  // Update video source when stream data becomes available
  useEffect(() => {
    if (!currentPlayerRef.current || !videoRef.current) return;

    const currentSrc = videoRef.current.src;
    if (currentSrc !== videoUrl) {
      console.log('VideoPlayer: Updating video source after stream data arrived', {
        filename,
        oldSrc: currentSrc?.substring(0, 100) + '...',
        newSrc: videoUrl?.substring(0, 100) + '...',
        hasStreamData: !!streamData,
        contentType: streamData?.contentType
      });

      try {
        // Update the video element source
        videoRef.current.src = videoUrl;
        videoRef.current.type = streamData?.contentType || 'video/mp4';

        console.log('VideoPlayer: Set video element src and type', {
          src: videoRef.current.src?.substring(0, 100) + '...',
          type: videoRef.current.type
        });

        // Force the video element to reload
        videoRef.current.load();

        console.log('VideoPlayer: Called video.load()');

        // Also try to update Plyr's source if available
        if (currentPlayerRef.current && currentPlayerRef.current.source) {
          console.log('VideoPlayer: Updating Plyr source');
          currentPlayerRef.current.source = {
            type: 'video',
            sources: [{
              src: videoUrl,
              type: streamData?.contentType || 'video/mp4'
            }]
          };
        }

        console.log('VideoPlayer: Source updated successfully');
      } catch (error) {
        console.error('VideoPlayer: Failed to update video source:', error);
      }
    }
  }, [videoUrl, streamData, filename]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentPlayerRef.current && typeof currentPlayerRef.current.destroy === 'function') {
        console.log('VideoPlayer: Cleaning up Plyr instance on unmount for', filename);
        currentPlayerRef.current.destroy();
        currentPlayerRef.current = null;
      }
    };
  }, [filename]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black/20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wedding-sage"></div>
      </div>
    );
  }

  return (
    <div className={`${!showControls ? 'video-player-slideshow' : ''} relative`}>
      <video
        ref={videoRef}
        key={`video-${filename}`}
        data-filename={filename}
        poster={posterUrl}
        playsInline
        muted={muted}
        preload={preload}
        controls={true} // Always enable native controls as fallback
        className={className}
        onLoadStart={(e) => console.log(`VideoPlayer: Load started for ${filename}`, { src: e.target.src?.substring(0, 100) + '...' })}
        onLoadedData={(e) => console.log(`VideoPlayer: Data loaded for ${filename}`, { readyState: e.target.readyState })}
        onCanPlay={(e) => console.log(`VideoPlayer: Can play for ${filename}`, { duration: e.target.duration })}
        onCanPlayThrough={(e) => console.log(`VideoPlayer: Can play through for ${filename}`)}
        onProgress={(e) => console.log(`VideoPlayer: Progress for ${filename}`, { buffered: e.target.buffered?.length, currentSrc: e.target.currentSrc?.substring(0, 100) + '...' })}
        onStalled={(e) => console.log(`VideoPlayer: Stalled for ${filename}`, { networkState: e.target.networkState })}
        onSuspend={(e) => console.log(`VideoPlayer: Suspended for ${filename}`)}
        onAbort={(e) => console.log(`VideoPlayer: Aborted for ${filename}`)}
        onWaiting={(e) => console.log(`VideoPlayer: Waiting for ${filename}`, { networkState: e.target.networkState })}
        onError={(e) => {
          const video = e.target;
          console.error(`VideoPlayer: Video error for ${filename}:`, {
            error: video.error,
            code: video.error?.code,
            message: video.error?.message,
            networkState: video.networkState,
            readyState: video.readyState,
            src: video.currentSrc,
            videoUrl: videoUrl
          });

          // If video fails to load, set poster to app logo (with cache-busting)
          if (video.poster !== `/JM Logo.svg?t=${Date.now()}`) {
            video.poster = `/JM Logo.svg?t=${Date.now()}`;
          }
          onError?.(video.error, video);
        }}
        onClick={(e) => {
          // If Plyr hasn't initialized yet, try to play the video natively
          const video = e.target;
          console.log(`VideoPlayer: Video clicked for ${filename}`, {
            paused: video.paused,
            controls: video.controls,
            hasPlyr: !!window.Plyr
          });
          if (video && video.paused && !video.controls) {
            video.play().catch(error => console.error('VideoPlayer: Play failed:', error));
          }
        }}
        onPlay={() => console.log(`VideoPlayer: Video playing for ${filename}`)}
        onPause={() => console.log(`VideoPlayer: Video paused for ${filename}`)}
      >
        {/* Let Plyr handle sources - we'll set them programmatically */}
        <p>Your browser doesn't support HTML5 video. Here's a <a href={videoUrl} target="_blank" rel="noopener noreferrer">direct link</a> to the video.</p>
      </video>

    </div>
  );
};

export default VideoPlayer;
